// src/components/Auth/EmailVerified.js
import React, { useEffect, useContext, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Container, Row, Col } from 'react-bootstrap';
import { AuthContext } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { broadcastCartSync } from '../Utils/cartSync';
import '../../styles/login.css';

const EmailVerified = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, hydrated: authHydrated, refreshUserProfile } = useContext(AuthContext);
  const { refreshCart, mergeCartItems, hasLocalCartItems, isHydrated: cartHydrated } = useCart();
  const [redirectCountdown, setRedirectCountdown] = useState(5);

  useEffect(() => {
    broadcastCartSync();
  }, []);

  // Check if user should be redirected to checkout after verification
  const redirectTo = searchParams.get('redirect') || localStorage.getItem('postVerificationRedirect');
  const shouldRedirectToCheckout = redirectTo === '/checkout/address';

  useEffect(() => {
    // Clear the stored redirect after checking it
    if (redirectTo) {
      localStorage.removeItem('postVerificationRedirect');
    }
  }, [redirectTo]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const isConfirmed = query.get('confirmed') === 'true';

    const canProceed = isAuthenticated && isConfirmed && authHydrated && cartHydrated;

    if (canProceed) {
      console.log('Email confirmed — refreshing user profile and checking local cart for merge.');
      
      // ✅ First, refresh user profile to get updated isEmailVerified status
      refreshUserProfile().then(() => {
        console.log('User profile refreshed after email verification');
      }).catch((error) => {
        console.error('Failed to refresh user profile after email verification:', error);
      });
      
      // Handle cart merging
      if (hasLocalCartItems()) {
        mergeCartItems();
      } else {
        refreshCart(); // fallback if nothing to merge
      }

      // If user should go to checkout, start countdown
      if (shouldRedirectToCheckout && redirectCountdown > 0) {
        const timer = setTimeout(() => {
          if (redirectCountdown === 1) {
            navigate('/checkout/address');
          } else {
            setRedirectCountdown(redirectCountdown - 1);
          }
        }, 1000);
        
        return () => clearTimeout(timer);
      }
    }
  }, [isAuthenticated, authHydrated, cartHydrated, hasLocalCartItems, mergeCartItems, refreshCart, refreshUserProfile, shouldRedirectToCheckout, redirectCountdown, navigate]);

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  const handleCheckoutRedirect = () => {
    navigate('/checkout/address');
  };

  const handleCancelRedirect = () => {
    setRedirectCountdown(0);
  };

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={6}>
          <div className="email-verified-container text-center">
            <h2 className="email-verified-heading">Email Verified! 🎉</h2>
            <Alert variant="success">
              Your email has been successfully verified! 
              {shouldRedirectToCheckout 
                ? ' You can now complete your checkout.' 
                : ' You can now access all features of your account.'
              }
            </Alert>

            {shouldRedirectToCheckout && isAuthenticated && redirectCountdown > 0 ? (
              <Alert variant="info" className="mt-3">
                <p><strong>Redirecting to checkout in {redirectCountdown} seconds...</strong></p>
                <div className="d-grid gap-2">
                  <button type="button" className="ff-btn ff-btn--primary" onClick={handleCheckoutRedirect}>
                    Continue to Checkout Now
                  </button>
                  <button type="button" className="ff-btn ff-btn--secondary ff-btn--sm" onClick={handleCancelRedirect}>
                    Cancel redirect
                  </button>
                </div>
              </Alert>
            ) : shouldRedirectToCheckout && isAuthenticated ? (
              <div className="d-grid gap-2 mt-3">
                <button type="button" className="ff-btn ff-btn--primary" onClick={handleCheckoutRedirect}>
                  Continue to Checkout
                </button>
                <button type="button" className="ff-btn ff-btn--secondary" onClick={() => navigate('/')}>
                  Go to Home
                </button>
              </div>
            ) : (
              <div className="d-grid gap-2 mt-3">
                {isAuthenticated ? (
                  <button type="button" className="ff-btn ff-btn--primary" onClick={() => navigate('/')}>
                    Continue Shopping
                  </button>
                ) : (
                  <button type="button" className="ff-btn ff-btn--primary" onClick={handleLoginRedirect}>
                    Go to Login
                  </button>
                )}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default EmailVerified;
